import React, { useState } from 'react';
import { GroupSettings, GroupMemberProfile, Match, Venue, AppState, FootballPosition, UserRole, FootballCardAttributes } from '../types';
import {
  Settings, Shield, DollarSign, Users, Award, Check, Sparkles, Bot, Code,
  MapPin, Calendar, RefreshCw, Download, Upload, Search, Edit3, Trash2, Plus, ArrowRight,
  MessageSquare, PhoneCall, Bell, Send, X
} from 'lucide-react';
import { generateSmsReminderUrl, generateWhatsappReminderUrl } from '../utils/timeUtils';
import { NumberInput } from './NumberInput';
import { calculatePositionOVR, getInitialPresetAttributes } from '../services/ratingCalculator';

interface AdminSettingsViewProps {
  state: AppState;
  onUpdateState: (newState: AppState) => void;
  onUpdateGroupSettings: (updatedGroup: GroupSettings) => void;
  onUpdateMember: (updatedMember: GroupMemberProfile) => void;
  onDeleteMember?: (memberId: string) => void;
  onResetState?: () => void;
  initialTab?: 'ai' | 'group' | 'members' | 'matches' | 'venues' | 'raw';
  requestsOnly?: boolean;
}

export const AdminSettingsView: React.FC<AdminSettingsViewProps> = ({
  state,
  onUpdateState,
  onUpdateGroupSettings,
  onUpdateMember,
  onDeleteMember,
  onResetState,
  initialTab = 'ai',
  requestsOnly = false
}) => {
  const [activeTab, setActiveTab] = useState<'ai' | 'group' | 'members' | 'matches' | 'venues' | 'raw'>(initialTab);
  const [settings, setSettings] = useState<GroupSettings>({ ...state.group });
  const [savedSuccess, setSavedSuccess] = useState(false);

  // AI Assistant Chat State
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiChatHistory, setAiChatHistory] = useState<Array<{ role: 'user' | 'assistant'; content: string; actions?: string[] }>>([
    {
      role: 'assistant',
      content: '👋 Hi Admin! I am your AI Backend Administrator. You can ask me to fix data, update jersey numbers, adjust member ratings, create matches, clear unpaid debts, or add venues in natural language!',
    }
  ]);

  // Member editing state
  const [selectedMember, setSelectedMember] = useState<GroupMemberProfile | null>(null);
  const [searchMember, setSearchMember] = useState('');
  const normalizedMemberSearch = searchMember.trim().toLowerCase();
  const filteredMembers = state.members.filter((m) => {
    if (!normalizedMemberSearch) return true;
    return [
      m.displayName,
      m.phoneNumber || '',
      m.whatsappNumber || '',
      m.primaryPosition || '',
      m.role || '',
    ].some((value) => value.toLowerCase().includes(normalizedMemberSearch));
  });
  const playerContacts = filteredMembers.filter((m) => m.role === 'player');
  const pendingMembers = state.members.filter(m => m.verificationStatus === 'pending');
  const approveMember = (pMem: GroupMemberProfile) => {
    onUpdateMember({ ...pMem, isVerified: true, verificationStatus: 'approved', isActive: true });
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };
  const rejectMember = (pMem: GroupMemberProfile) => {
    onUpdateMember({ ...pMem, isVerified: false, verificationStatus: 'rejected', isActive: false });
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  // Full editable stat field catalog (matches the organizer Manage Player modal).
  const STAT_FIELDS: {
    key: keyof GroupMemberProfile['stats'];
    label: string;
    color: string;
    allowDecimal?: boolean;
    min?: number;
    max?: number;
  }[] = [
    { key: 'matchesPlayed', label: 'Matches', color: 'text-emerald-400' },
    { key: 'wins', label: 'Wins', color: 'text-green-400' },
    { key: 'draws', label: 'Draws', color: 'text-slate-300' },
    { key: 'losses', label: 'Losses', color: 'text-red-400' },
    { key: 'goals', label: 'Goals', color: 'text-emerald-400' },
    { key: 'assists', label: 'Assists', color: 'text-blue-400' },
    { key: 'cleanSheets', label: 'Clean Sheets', color: 'text-teal-400' },
    { key: 'motmCount', label: 'MOTM', color: 'text-amber-400' },
    { key: 'hatTricks', label: 'Hat-Tricks', color: 'text-purple-400' },
    { key: 'yellowCards', label: 'Yellow Cards', color: 'text-yellow-400' },
    { key: 'redCards', label: 'Red Cards', color: 'text-red-500' },
    { key: 'ownGoals', label: 'Own Goals', color: 'text-orange-400' },
    { key: 'lateArrivals', label: 'Late Arrivals', color: 'text-amber-300' },
    { key: 'noShows', label: 'No-Shows', color: 'text-red-400' },
    { key: 'attendancePct', label: 'Attendance %', color: 'text-sky-400', min: 0, max: 100 },
    { key: 'winPct', label: 'Win %', color: 'text-green-400', min: 0, max: 100 },
    { key: 'averageRating', label: 'Avg Rating', color: 'text-indigo-400', allowDecimal: true, min: 1, max: 10 },
  ];

  const updateSelectedStat = (key: keyof GroupMemberProfile['stats'], value: number) => {
    setSelectedMember((prev) => (prev ? { ...prev, stats: { ...prev.stats, [key]: value } } : prev));
  };

  const updateSelectedCardAttr = (key: keyof FootballCardAttributes, value: number) => {
    setSelectedMember((prev) => {
      if (!prev) return prev;
      const nextCard = { ...prev.footballCard, [key]: value };
      nextCard.overall = calculatePositionOVR(prev.primaryPosition, nextCard);
      return { ...prev, footballCard: nextCard };
    });
  };

  const handleDeleteSelected = () => {
    if (!onDeleteMember || !selectedMember) return;
    if (!window.confirm(`Delete player "${selectedMember.displayName}" permanently? This removes their profile, login account and sessions. This cannot be undone.`)) return;
    onDeleteMember(selectedMember.id);
    setSelectedMember(null);
  };

  // Raw JSON Inspector State
  const [rawJsonTab, setRawJsonTab] = useState<keyof AppState>('group');
  const [rawJsonText, setRawJsonText] = useState(JSON.stringify(state.group, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Venue creation state
  const [newVenueName, setNewVenueName] = useState('');
  const [newVenueAddress, setNewVenueAddress] = useState('');
  const [newVenuePrice, setNewVenuePrice] = useState(2000);

  const handleSaveGroup = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdateGroupSettings(settings);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 3000);
  };

  // AI Assistant Submission
  const handleAiSubmit = async (promptToRun?: string) => {
    const prompt = promptToRun || aiPrompt;
    if (!prompt.trim() || isAiLoading) return;

    setAiChatHistory(prev => [...prev, { role: 'user', content: prompt }]);
    if (!promptToRun) setAiPrompt('');
    setIsAiLoading(true);

    try {
      const res = await fetch('/api/admin/ai-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, currentState: state })
      });

      if (!res.ok) throw new Error('Backend assistant request failed');

      const data = (await res.json()) as {
        reply?: string;
        actionsExecuted?: string[];
        updatedState?: AppState | null;
      };
      setAiChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || 'State updated successfully.',
          actions: data.actionsExecuted
        }
      ]);

      if (data.updatedState) {
        onUpdateState(data.updatedState);
      }
    } catch (err: any) {
      setAiChatHistory(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `⚠️ Error contacting backend AI agent: ${err.message || 'Server error'}. Please try again.`
        }
      ]);
    } finally {
      setIsAiLoading(false);
    }
  };

  // Raw JSON Save Handler
  const handleSaveRawJson = () => {
    try {
      const parsed = JSON.parse(rawJsonText);
      const nextState = { ...state, [rawJsonTab]: parsed };
      onUpdateState(nextState);
      setJsonError(null);
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err: any) {
      setJsonError(`Invalid JSON: ${err.message}`);
    }
  };

  // Add Venue Handler
  const handleAddVenue = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVenueName) return;
    const newV: Venue = {
      id: `ven_${Date.now()}`,
      name: newVenueName,
      address: newVenueAddress || 'Dhaka, Bangladesh',
      googleMapsUrl: 'https://maps.google.com',
      latitude: 23.8103,
      longitude: 90.4125,
      phoneNumber: '+880 1700 000000',
      imageUrl: 'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=800&auto=format&fit=crop&q=80',
      turfSize: '7v7 Synthetic Grass',
      pricePerHour: newVenuePrice,
      isIndoor: false,
      rating: 4.8,
      notes: 'Standard 7v7 turf ground with floodlights and parking.',
      totalMatchesPlayed: 0
    };
    onUpdateState({ ...state, venues: [...state.venues, newV] });
    setNewVenueName('');
    setNewVenueAddress('');
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-20">
      
      {/* Title Header */}
      <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-wrap items-center justify-between gap-4 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/40 rounded-2xl text-emerald-400">
            <Shield className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-black text-white">Master Admin Control Panel</h2>
              <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">
                Backend System Mode
              </span>
            </div>
            <p className="text-xs text-slate-400 font-medium">
              Manage member profiles, fix matches, adjust OVR ratings, or use the AI Assistant agent to modify backend data.
            </p>
          </div>
        </div>

        {savedSuccess && (
          <div className="bg-emerald-500 text-slate-950 font-extrabold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 shadow-lg shadow-emerald-500/20">
            <Check className="w-4 h-4 stroke-[3]" /> Changes Applied to Backend!
          </div>
        )}
      </div>

      {/* Admin Navigation Sub-Tabs */}
      {!requestsOnly && (
      <div className="flex border-b border-slate-800 bg-slate-900/90 p-1.5 rounded-2xl overflow-x-auto no-scrollbar text-xs font-extrabold text-slate-400 gap-1">
        {[
          { id: 'ai', label: '🤖 AI Backend Agent Chat', icon: Bot, highlight: true },
          { id: 'members', label: '👥 Member Profiles Manager', icon: Users },
          { id: 'group', label: '⚙️ Group & Rules Config', icon: Settings },
          { id: 'matches', label: '⚽ Matches & RSVPs', icon: Calendar },
          { id: 'venues', label: '🏟️ Turfs & Venues', icon: MapPin },
          { id: 'raw', label: '📜 Database Inspector (JSON)', icon: Code },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id as any);
                if (tab.id === 'raw') {
                  setRawJsonText(JSON.stringify(state[rawJsonTab], null, 2));
                }
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition whitespace-nowrap ${
                isActive
                  ? 'bg-emerald-500 text-slate-950 font-black shadow-lg shadow-emerald-500/20'
                  : tab.highlight
                  ? 'bg-slate-800/80 text-lime-400 hover:bg-slate-800 border border-lime-400/30'
                  : 'hover:bg-slate-800 text-slate-300'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      )}

      {/* QUICK REQUEST APPROVAL QUEUE */}
      {requestsOnly && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-5 shadow-2xl relative z-10 pointer-events-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-800 pb-4">
            <div>
              <h3 className="text-lg font-black text-white">Player Approval Queue</h3>
              <p className="text-xs text-slate-400">When someone registers, review them here and approve directly from your admin panel.</p>
            </div>
            <div className="text-[10px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-xl">
              {pendingMembers.length} pending
            </div>
          </div>

          {pendingMembers.length === 0 ? (
            <div className="bg-slate-950 border border-dashed border-slate-700 rounded-2xl p-6 text-center text-sm text-slate-400">
              No pending player requests right now.
            </div>
          ) : (
            <div className="space-y-3">
              {pendingMembers.map((pMem) => {
                const whatsappTarget = pMem.whatsappNumber || pMem.phoneNumber || '+880';
                return (
                  <div key={`queue-${pMem.id}`} className="bg-slate-950 border border-amber-500/20 rounded-2xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={pMem.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'} alt={pMem.displayName} className="w-11 h-11 rounded-xl object-cover border border-amber-500/30" />
                        <div className="min-w-0">
                          <div className="font-extrabold text-white text-sm truncate">{pMem.displayName}</div>
                          <div className="text-[11px] text-slate-400 font-mono truncate">#{pMem.jerseyNumber} • {pMem.primaryPosition} • {pMem.preferredFoot}</div>
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-lg">Pending Review</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Phone</div>
                        <div className="font-mono text-slate-200 break-all">{pMem.phoneNumber || 'Missing phone number'}</div>
                      </div>
                      <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">WhatsApp</div>
                        <div className="font-mono text-slate-300 break-all">{pMem.whatsappNumber || 'No WhatsApp provided'}</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 relative z-20 pointer-events-auto">
                      <a href={generateSmsReminderUrl(pMem.phoneNumber || '+880', state.matches[0]?.title || 'GigaBalls FC Matchday', state.matches[0]?.date || 'Upcoming', state.matches[0]?.startTime || '21:00', state.matches[0]?.venueName || 'Main Pitch')} className="px-3 py-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/30 text-xs font-bold rounded-xl transition flex items-center gap-1.5">
                        <Send className="w-3.5 h-3.5" /> SMS
                      </a>
                      <a href={generateWhatsappReminderUrl(whatsappTarget, state.matches[0]?.title || 'GigaBalls FC Matchday', state.matches[0]?.date || 'Upcoming', state.matches[0]?.startTime || '21:00', state.matches[0]?.venueName || 'Main Pitch')} target="_blank" rel="noopener noreferrer" className="px-3 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-xs font-bold rounded-xl transition flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                      <button type="button" onClick={() => approveMember(pMem)} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-black rounded-xl transition">
                        Approve Player
                      </button>
                      <button type="button" onClick={() => rejectMember(pMem)} className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 text-xs font-bold rounded-xl transition">
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 1: AI BACKEND AGENT CHAT */}
      {activeTab === 'ai' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-6 shadow-2xl">
          <div className="flex items-center justify-between pb-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-lime-400/10 border border-lime-400/30 text-lime-400">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white flex items-center gap-2">
                  AI System Admin Agent
                  <span className="text-[10px] font-mono bg-lime-400/20 text-lime-300 px-2 py-0.5 rounded border border-lime-400/30">
                    Smart Rules Engine
                  </span>
                </h3>
                <p className="text-xs text-slate-400">Instruct the backend agent to fix data, modify profiles, or query stats via natural language</p>
              </div>
            </div>
          </div>

          {/* Prompt Suggestions Chips */}
          <div className="space-y-2">
            <span className="text-[11px] font-extrabold uppercase text-slate-400 tracking-wider">Quick Actions:</span>
            <div className="flex flex-wrap gap-2 text-xs">
              {[
                "Set Saif's group jersey number to #10",
                "Increase Saif OVR rating to 88",
                "Clear all member unpaid debt balances",
                "Create a new 7v7 turf match for Friday at 9 PM",
                "Post an announcement about upcoming weekend tournament"
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAiSubmit(suggestion)}
                  disabled={isAiLoading}
                  className="px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-lime-400/50 text-slate-300 hover:text-white text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Chat Logs Window */}
          <div className="bg-slate-950 border border-slate-800/80 rounded-2xl p-4 min-h-[280px] max-h-[420px] overflow-y-auto space-y-4">
            {aiChatHistory.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-xl bg-lime-400 text-slate-950 font-black flex items-center justify-center shrink-0">
                    <Bot className="w-5 h-5" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] p-4 rounded-2xl text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-emerald-500 text-slate-950 font-bold rounded-tr-none'
                      : 'bg-slate-900 border border-slate-800 text-slate-200 rounded-tl-none space-y-2'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-800 space-y-1 text-[11px]">
                      <span className="font-extrabold text-lime-400 uppercase tracking-wider block">Actions Executed:</span>
                      {msg.actions.map((act, i) => (
                        <div key={i} className="flex items-center gap-1 text-slate-300">
                          <Check className="w-3 h-3 text-emerald-400 shrink-0" /> {act}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isAiLoading && (
              <div className="flex items-center gap-3 text-xs text-lime-400 font-mono animate-pulse p-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>AI Agent is analyzing system state and applying requested mutations...</span>
              </div>
            )}
          </div>

          {/* Input Bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAiSubmit();
            }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. Set jersey 7 for Tanvir, or clear all unpaid debts..."
              className="flex-1 bg-slate-950 border border-slate-800 text-white rounded-2xl p-3 text-xs font-semibold focus:outline-none focus:border-lime-400"
            />
            <button
              type="submit"
              disabled={isAiLoading || !aiPrompt.trim()}
              className="px-6 py-3 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-slate-950 font-black text-xs rounded-2xl transition flex items-center gap-2 shadow-lg shadow-lime-400/20"
            >
              <span>Execute</span>
              <ArrowRight className="w-4 h-4 stroke-[3]" />
            </button>
          </form>
        </div>
      )}

      {/* TAB 2: MEMBER PROFILES MANAGER */}
      {activeTab === 'members' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-6 shadow-2xl">
          <div className="flex flex-wrap justify-between items-center gap-4 border-b border-slate-800 pb-4">
            <div>
              <h3 className="text-lg font-black text-white">Group Member Profiles ({state.members.length})</h3>
              <p className="text-xs text-slate-400">Manage admin verifications, jersey numbers, FIFA ratings, roles, debts, and direct contact info.</p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search member, phone, WhatsApp..."
                value={searchMember}
                onChange={(e) => setSearchMember(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 text-white text-xs rounded-xl pl-9 p-2.5 focus:outline-none"
              />
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h4 className="text-sm font-black text-white">Player Contact Directory</h4>
                <p className="text-[11px] text-slate-400">Quick lookup for organizer contact. Active phone is required; WhatsApp is optional.</p>
              </div>
              <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-lg">
                {playerContacts.length} player{playerContacts.length === 1 ? '' : 's'} shown
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {playerContacts.length > 0 ? playerContacts.map((mem) => {
                const whatsappTarget = mem.whatsappNumber || mem.phoneNumber || '+880';
                return (
                  <div key={`contact-${mem.id}`} className="bg-slate-900 border border-slate-800 rounded-2xl p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-extrabold text-white text-sm truncate">{mem.displayName}</div>
                        <div className="text-[11px] text-slate-400 font-mono truncate">#{mem.jerseyNumber} • {mem.primaryPosition} • {mem.role.toUpperCase()}</div>
                      </div>
                      <button
                        onClick={() => setSelectedMember(mem)}
                        className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-lime-400 text-[10px] font-black rounded-lg border border-slate-700 transition shrink-0"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2 text-slate-200">
                        <PhoneCall className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <span className="font-mono break-all">{mem.phoneNumber || 'Missing phone number'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <MessageSquare className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span className="font-mono break-all">{mem.whatsappNumber || 'No WhatsApp provided'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <a
                        href={generateSmsReminderUrl(mem.phoneNumber || '+880', state.matches[0]?.title || 'GigaBalls FC Matchday', state.matches[0]?.date || 'Upcoming', state.matches[0]?.startTime || '21:00', state.matches[0]?.venueName || 'Main Pitch')}
                        className="px-2.5 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 text-[10px] font-bold rounded-lg transition flex items-center gap-1"
                      >
                        <Send className="w-3 h-3" /> SMS
                      </a>
                      <a
                        href={generateWhatsappReminderUrl(whatsappTarget, state.matches[0]?.title || 'GigaBalls FC Matchday', state.matches[0]?.date || 'Upcoming', state.matches[0]?.startTime || '21:00', state.matches[0]?.venueName || 'Main Pitch')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold rounded-lg transition flex items-center gap-1"
                      >
                        <MessageSquare className="w-3 h-3" /> WhatsApp
                      </a>
                    </div>
                  </div>
                );
              }) : (
                <div className="lg:col-span-2 text-xs text-slate-500 bg-slate-900 border border-dashed border-slate-700 rounded-2xl p-4 text-center">
                  No players matched your search.
                </div>
              )}
            </div>
          </div>

          {/* Pending Verification Requests Widget */}
          {(() => {
            if (pendingMembers.length === 0) return null;

            return (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-amber-300 uppercase tracking-wider flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-400" /> Pending Player Requests ({pendingMembers.length})
                  </span>
                  <span className="text-[10px] font-mono text-amber-400 font-bold bg-amber-500/20 px-2 py-0.5 rounded border border-amber-500/30">
                    Action Required
                  </span>
                </div>

                <div className="space-y-2">
                  {pendingMembers.map((pMem) => (
                    <div key={pMem.id} className="bg-slate-950 p-3 rounded-xl border border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
                      <div className="flex items-center gap-3">
                        <img src={pMem.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'} alt={pMem.displayName} className="w-9 h-9 rounded-full object-cover border border-amber-500/40" />
                        <div>
                          <div className="font-extrabold text-white flex items-center gap-2">
                            {pMem.displayName}
                            <span className="text-[10px] font-mono text-slate-400">User ID: {pMem.userId}</span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono block">#{pMem.jerseyNumber} • {pMem.primaryPosition} • Foot: {pMem.preferredFoot}</span>
                          <span className="text-[10px] text-slate-400 font-mono block">Phone: {pMem.phoneNumber || 'Missing'}{pMem.whatsappNumber ? ` • WhatsApp: ${pMem.whatsappNumber}` : ''}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => approveMember(pMem)}
                          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl shadow transition"
                        >
                          ✓ Approve & Verify
                        </button>
                        <button
                          onClick={() => rejectMember(pMem)}
                          className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold text-xs rounded-xl border border-red-500/30 transition"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredMembers
              .map((mem) => (
                <div key={mem.id} className="bg-slate-950 border border-slate-800/80 p-4 rounded-2xl space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center font-black text-emerald-400 text-lg">
                        #{mem.jerseyNumber}
                      </div>
                      <div>
                        <h4 className="font-extrabold text-sm text-white">{mem.displayName}</h4>
                        <div className="text-[11px] text-slate-400 flex items-center gap-2 font-mono">
                          <span className="text-amber-300 font-bold">OVR {mem.footballCard.overall}</span> • <span>{mem.primaryPosition}</span> • <span className="uppercase text-emerald-400">{mem.role}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setSelectedMember(mem)}
                      className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-lime-400 text-xs font-black rounded-xl border border-slate-700 transition flex items-center gap-1"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Edit Profile
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono bg-slate-900/60 p-2 rounded-xl border border-slate-800">
                    <div>
                      <span className="text-slate-400 block font-sans">Debt Balance</span>
                      <span className={mem.walletBalance > 0 ? "text-red-400 font-extrabold" : "text-emerald-400 font-bold"}>
                        ৳{mem.walletBalance}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-sans">Goals / Assists</span>
                      <span className="text-slate-200 font-bold">{mem.stats.goals} G / {mem.stats.assists} A</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block font-sans">MOTM</span>
                      <span className="text-amber-400 font-bold">{mem.stats.motmCount} 🏆</span>
                    </div>
                  </div>

                  {/* Direct SMS & WhatsApp Quick Buttons for Admin */}
                  <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-800/60">
                    <div className="text-[10px] font-mono text-slate-400 min-w-0">
                      <div className="truncate">📱 {mem.phoneNumber || 'Missing phone number'}</div>
                      <div className="truncate">💬 {mem.whatsappNumber || 'No WhatsApp provided'}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <a
                        href={generateSmsReminderUrl(
                          mem.phoneNumber || '+880****5678',
                          state.matches[0]?.title || 'GigaBalls FC Matchday',
                          state.matches[0]?.date || 'Upcoming',
                          state.matches[0]?.startTime || '21:00',
                          state.matches[0]?.venueName || 'Main Pitch'
                        )}
                        className="px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 text-[10px] font-bold rounded-lg transition flex items-center gap-1"
                        title="Send Direct SMS"
                      >
                        <Send className="w-3 h-3 text-blue-400" /> SMS
                      </a>
                      <a
                        href={generateWhatsappReminderUrl(
                          mem.whatsappNumber || mem.phoneNumber || '+880****5678',
                          state.matches[0]?.title || 'GigaBalls FC Matchday',
                          state.matches[0]?.date || 'Upcoming',
                          state.matches[0]?.startTime || '21:00',
                          state.matches[0]?.venueName || 'Main Pitch'
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold rounded-lg transition flex items-center gap-1"
                        title="Open WhatsApp Chat"
                      >
                        <MessageSquare className="w-3 h-3 text-emerald-400" /> WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Member Edit Modal */}
          {selectedMember && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <div className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
                <div className="px-5 py-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
                  <div>
                    <h3 className="font-black text-lg text-white">Edit Player: {selectedMember.displayName}</h3>
                    <p className="text-xs text-slate-400">Full admin control — profile, card, stats &amp; finances</p>
                  </div>
                  <button onClick={() => setSelectedMember(null)} className="p-2 bg-slate-800 text-slate-400 hover:text-white rounded-xl">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4 sm:p-5 overflow-y-auto space-y-5 text-xs">

                  {/* Identity & Contact */}
                  <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                    <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-emerald-400">Identity &amp; Contact</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Display Name</label>
                        <input
                          type="text"
                          value={selectedMember.displayName}
                          onChange={(e) => setSelectedMember({ ...selectedMember, displayName: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-white font-medium rounded-xl p-2.5 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Jersey Number</label>
                        <NumberInput
                          value={selectedMember.jerseyNumber}
                          onCommit={(v) => setSelectedMember({ ...selectedMember, jerseyNumber: v })}
                          min={0}
                          className="w-full bg-slate-900 border border-slate-700 text-amber-300 font-mono font-bold rounded-xl p-2.5"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Active Phone Number</label>
                        <input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          value={selectedMember.phoneNumber || ''}
                          onChange={(e) => setSelectedMember({ ...selectedMember, phoneNumber: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-white font-mono p-2.5 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">WhatsApp Number</label>
                        <input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          value={selectedMember.whatsappNumber || ''}
                          onChange={(e) => setSelectedMember({ ...selectedMember, whatsappNumber: e.target.value })}
                          className="w-full bg-slate-900 border border-slate-700 text-slate-200 font-mono p-2.5 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Group Role</label>
                        <select
                          value={selectedMember.role}
                          onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value as UserRole })}
                          className="w-full bg-slate-900 border border-slate-700 text-emerald-400 font-bold rounded-xl p-2.5"
                        >
                          <option value="owner">Owner</option>
                          <option value="admin">Admin</option>
                          <option value="organizer">Organizer</option>
                          <option value="player">Player</option>
                          <option value="guest">Guest</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Primary Position</label>
                        <select
                          value={selectedMember.primaryPosition}
                          onChange={(e) => setSelectedMember({ ...selectedMember, primaryPosition: e.target.value as FootballPosition })}
                          className="w-full bg-slate-900 border border-slate-700 text-white font-mono rounded-xl p-2.5"
                        >
                          {['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'ST'].map(pos => (
                            <option key={pos} value={pos}>{pos}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Football Card Attributes */}
                  <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-[#a8c936]">Football Card Attributes</h4>
                      <span className="text-sm font-extrabold font-mono text-white bg-slate-900 px-2.5 py-0.5 rounded-lg border border-slate-700">
                        OVR {selectedMember.footballCard.overall}
                      </span>
                    </div>

                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {['Beginner', 'Average', 'Good', 'Very Good', 'Excellent', 'Custom'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => {
                            if (p === 'Custom') return;
                            const attrs = { ...getInitialPresetAttributes(p) };
                            attrs.overall = calculatePositionOVR(selectedMember.primaryPosition, attrs);
                            setSelectedMember({ ...selectedMember, footballCard: attrs });
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition shrink-0 ${
                            p === 'Custom' ? 'bg-slate-800 text-slate-400' : 'bg-slate-900 text-[#a8c936] border border-slate-700'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Pace', key: 'pace' },
                        { label: 'Shooting', key: 'shooting' },
                        { label: 'Passing', key: 'passing' },
                        { label: 'Dribbling', key: 'dribbling' },
                        { label: 'Defending', key: 'defending' },
                        { label: 'Physical', key: 'physical' },
                      ].map((item) => (
                        <div key={item.key} className="bg-slate-900 px-2 py-2 rounded-xl border border-slate-800 text-center">
                          <span className="block text-[11px] text-slate-400 font-bold mb-1">{item.label}</span>
                          <NumberInput
                            value={(selectedMember.footballCard as any)[item.key]}
                            onCommit={(v) => updateSelectedCardAttr(item.key as any, v)}
                            min={1}
                            max={99}
                            emptyDefault={60}
                            className="w-full bg-slate-950 text-amber-300 font-mono font-black text-center rounded border border-slate-700 py-1.5"
                          />
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="block text-slate-300 font-bold mb-1">Overall OVR Rating</label>
                      <NumberInput
                        value={selectedMember.footballCard.overall}
                        onCommit={(v) => setSelectedMember({ ...selectedMember, footballCard: { ...selectedMember.footballCard, overall: v } })}
                        min={1}
                        max={99}
                        emptyDefault={75}
                        className="w-full bg-slate-900 border border-slate-700 text-amber-300 font-mono font-extrabold rounded-xl p-2.5"
                      />
                    </div>
                  </div>

                  {/* Career Stats */}
                  <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                    <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-blue-400">
                      Career Stats &amp; Attendance (all editable)
                    </h4>
                    <div className="grid grid-cols-2 min-[480px]:grid-cols-3 sm:grid-cols-4 gap-2.5 text-center font-mono">
                      {STAT_FIELDS.map((f) => (
                        <div key={f.key} className="bg-slate-900 px-2 pt-2 pb-1.5 rounded-xl">
                          <span className="text-[10px] sm:text-[11px] text-slate-400 block font-sans leading-snug min-h-[12px]">{f.label}</span>
                          <NumberInput
                            value={selectedMember.stats[f.key] ?? 0}
                            onCommit={(v) => updateSelectedStat(f.key, v)}
                            allowDecimal={f.allowDecimal}
                            min={f.min}
                            max={f.max}
                            className={`w-full bg-slate-950 ${f.color} font-bold text-center rounded border border-slate-700 px-1 py-1.5`}
                            aria-label={f.label}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Finances & Notes */}
                  <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
                    <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-emerald-400">Finances &amp; Notes</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Debt / Wallet Balance (৳)</label>
                        <NumberInput
                          value={selectedMember.walletBalance}
                          onCommit={(v) => setSelectedMember({ ...selectedMember, walletBalance: v })}
                          min={0}
                          className="w-full bg-slate-900 border border-slate-700 text-emerald-400 font-mono font-bold rounded-xl p-2.5"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-300 font-bold mb-1">Internal Notes</label>
                        <input
                          type="text"
                          value={selectedMember.internalNotes || ''}
                          onChange={(e) => setSelectedMember({ ...selectedMember, internalNotes: e.target.value })}
                          placeholder="Private organizer notes..."
                          className="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-xl p-2.5"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-slate-300 font-bold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMember.isActive}
                        onChange={(e) => setSelectedMember({ ...selectedMember, isActive: e.target.checked })}
                        className="w-4 h-4 accent-emerald-500"
                      />
                      Active player
                    </label>
                  </div>

                  {/* Footer Actions */}
                  {onDeleteMember && (
                    <button
                      type="button"
                      onClick={handleDeleteSelected}
                      className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-black text-sm rounded-xl border border-red-500/40 transition flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete Player Permanently
                    </button>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        onUpdateMember(selectedMember);
                        setSelectedMember(null);
                        setSavedSuccess(true);
                        setTimeout(() => setSavedSuccess(false), 3000);
                      }}
                      className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm rounded-xl shadow"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setSelectedMember(null)}
                      className="px-4 py-3 bg-slate-800 text-slate-300 font-bold text-xs rounded-xl"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: GROUP & RULES CONFIG */}
      {activeTab === 'group' && (
        <form onSubmit={handleSaveGroup} className="space-y-6">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-4 shadow-2xl">
            <h3 className="font-extrabold text-sm uppercase text-emerald-400 tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4" /> Group Identity & Location
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Group Name</label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 text-white font-semibold rounded-xl p-3 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Join Code</label>
                <input
                  type="text"
                  value={settings.joinCode}
                  onChange={(e) => setSettings({ ...settings, joinCode: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 text-amber-300 font-mono font-bold rounded-xl p-3 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Currency Symbol</label>
                <input
                  type="text"
                  value={settings.currency}
                  onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 text-emerald-400 font-mono font-bold rounded-xl p-3 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Timezone</label>
                <input
                  type="text"
                  value={settings.timezone}
                  onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 text-slate-300 rounded-xl p-3 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-4 shadow-2xl">
            <h3 className="font-extrabold text-sm uppercase text-blue-400 tracking-wider">
              Match Defaults & Financial Rules
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Default Turf Cost (৳)</label>
                <NumberInput
                  value={settings.finance.defaultTurfCost}
                  onCommit={(v) => setSettings({ ...settings, finance: { ...settings.finance, defaultTurfCost: v } })}
                  min={0}
                  emptyDefault={2100}
                  className="w-full bg-slate-950 border border-slate-800 text-emerald-400 font-mono font-bold p-2.5 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">bKash Merchant/Personal</label>
                <input
                  type="text"
                  value={settings.finance.bkashNumber}
                  onChange={(e) => setSettings({
                    ...settings,
                    finance: { ...settings.finance, bkashNumber: e.target.value }
                  })}
                  className="w-full bg-slate-950 border border-slate-800 text-pink-400 font-mono font-bold p-2.5 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Nagad Merchant/Personal</label>
                <input
                  type="text"
                  value={settings.finance.nagadNumber}
                  onChange={(e) => setSettings({
                    ...settings,
                    finance: { ...settings.finance, nagadNumber: e.target.value }
                  })}
                  className="w-full bg-slate-950 border border-slate-800 text-orange-400 font-mono font-bold p-2.5 rounded-xl"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm rounded-2xl transition shadow-xl shadow-emerald-500/20"
          >
            Save Group Settings 💾
          </button>
        </form>
      )}

      {/* TAB 4: MATCHES & RSVPS OVERRIDE */}
      {activeTab === 'matches' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-6 shadow-2xl">
          <div className="flex justify-between items-center border-b border-slate-800 pb-4">
            <div>
              <h3 className="text-lg font-black text-white">Matches & Status Overrides ({state.matches.length})</h3>
              <p className="text-xs text-slate-400">Force match status, adjust total fees, or reset RSVPs.</p>
            </div>
          </div>

          <div className="space-y-4">
            {state.matches.map((m) => (
              <div key={m.id} className="bg-slate-950 border border-slate-800 p-4 rounded-2xl flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h4 className="font-extrabold text-sm text-white">{m.title}</h4>
                  <p className="text-xs text-slate-400 font-mono">
                    {m.date} • {m.startTime}-{m.endTime} • {m.venueName} • Fee: ৳{m.totalTurfFee}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={m.status}
                    onChange={(e) => {
                      const nextMatches = state.matches.map(x => x.id === m.id ? { ...x, status: e.target.value as any } : x);
                      onUpdateState({ ...state, matches: nextMatches });
                    }}
                    className="bg-slate-900 border border-slate-800 text-emerald-400 font-mono text-xs font-bold p-2 rounded-xl"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>

                  <button
                    onClick={() => {
                      const nextRsvps = state.rsvps.filter(r => r.matchId !== m.id);
                      onUpdateState({ ...state, rsvps: nextRsvps });
                      alert(`Reset all RSVPs for match ${m.title}`);
                    }}
                    className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-bold rounded-xl transition"
                  >
                    Reset RSVPs
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 5: TURFS & VENUES */}
      {activeTab === 'venues' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-6 shadow-2xl">
          <div className="border-b border-slate-800 pb-4">
            <h3 className="text-lg font-black text-white">Turf Venues Directory Manager</h3>
            <p className="text-xs text-slate-400">Add or edit partner turf grounds and hourly rental costs.</p>
          </div>

          <form onSubmit={handleAddVenue} className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
            <h4 className="font-extrabold text-xs uppercase text-lime-400 flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add New Turf Venue
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <input
                type="text"
                placeholder="Venue Name (e.g. Uttara Arena)"
                value={newVenueName}
                onChange={(e) => setNewVenueName(e.target.value)}
                className="bg-slate-900 border border-slate-800 text-white rounded-xl p-2.5"
              />
              <input
                type="text"
                placeholder="Address"
                value={newVenueAddress}
                onChange={(e) => setNewVenueAddress(e.target.value)}
                className="bg-slate-900 border border-slate-800 text-white rounded-xl p-2.5"
              />
              <NumberInput
                value={newVenuePrice}
                onCommit={setNewVenuePrice}
                min={0}
                emptyDefault={2000}
                placeholder="Hourly Price (৳)"
                className="bg-slate-900 border border-slate-800 text-emerald-400 font-mono font-bold rounded-xl p-2.5"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 bg-emerald-500 text-slate-950 font-black text-xs rounded-xl shadow"
            >
              Add Venue
            </button>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {state.venues.map((v) => (
              <div key={v.id} className="bg-slate-950 border border-slate-800 p-4 rounded-2xl space-y-1">
                <div className="flex justify-between items-center">
                  <h4 className="font-extrabold text-sm text-white">{v.name}</h4>
                  <span className="text-xs font-mono font-extrabold text-emerald-400">৳{v.pricePerHour}/hr</span>
                </div>
                <p className="text-xs text-slate-400">{v.address}</p>
                <p className="text-[11px] text-slate-500 font-mono">{v.turfSize}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 6: RAW DATABASE INSPECTOR & JSON FIXER */}
      {activeTab === 'raw' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-6 shadow-2xl">
          <div className="flex flex-wrap justify-between items-center gap-4 border-b border-slate-800 pb-4">
            <div>
              <h3 className="text-lg font-black text-white flex items-center gap-2">
                Raw AppState Inspector & JSON Override
                <span className="text-[10px] font-mono bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded">
                  Direct DB Edit
                </span>
              </h3>
              <p className="text-xs text-slate-400">Inspect or directly modify backend raw JSON records</p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(state, null, 2));
                  const downloadAnchor = document.createElement('a');
                  downloadAnchor.setAttribute("href", dataStr);
                  downloadAnchor.setAttribute("download", `blank_fc_backup_${Date.now()}.json`);
                  document.body.appendChild(downloadAnchor);
                  downloadAnchor.click();
                  downloadAnchor.remove();
                }}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-xl border border-slate-700 flex items-center gap-1.5"
              >
                <Download className="w-4 h-4 text-emerald-400" /> Export JSON
              </button>

              {onResetState && (
                <button
                  onClick={() => {
                    if (confirm("Reset application state to original default seed data?")) {
                      onResetState();
                    }
                  }}
                  className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-xl border border-red-500/30 flex items-center gap-1.5"
                >
                  <RefreshCw className="w-4 h-4" /> Reset DB
                </button>
              )}
            </div>
          </div>

          {/* Collection Switcher */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar text-xs font-bold font-mono">
            {(['group', 'members', 'matches', 'venues', 'payments', 'announcements', 'rsvps'] as Array<keyof AppState>).map((key) => (
              <button
                key={key}
                onClick={() => {
                  setRawJsonTab(key);
                  setRawJsonText(JSON.stringify(state[key], null, 2));
                  setJsonError(null);
                }}
                className={`px-3 py-1.5 rounded-xl border transition uppercase ${
                  rawJsonTab === key
                    ? 'bg-amber-400 text-slate-950 border-amber-400 font-extrabold'
                    : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-white'
                }`}
              >
                {key}
              </button>
            ))}
          </div>

          {jsonError && (
            <div className="p-3 bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-mono rounded-xl">
              {jsonError}
            </div>
          )}

          <div className="space-y-3">
            <textarea
              value={rawJsonText}
              onChange={(e) => {
                setRawJsonText(e.target.value);
                setJsonError(null);
              }}
              rows={16}
              className="w-full bg-slate-950 border border-slate-800 text-lime-300 font-mono text-xs rounded-2xl p-4 focus:outline-none focus:border-amber-400 leading-relaxed"
            />

            <button
              onClick={handleSaveRawJson}
              className="w-full py-3.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs rounded-xl shadow-lg transition"
            >
              Apply JSON Override to Collection '{rawJsonTab}' ⚡
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
